import { ActionMenu } from 'common-ui/components/Table/ActionMenu'
import { ActionMenuRow } from 'common-ui/components/Table/ActionMenuContext'
import { useEffect, useRef, useState } from 'react'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import IconButton from '@mui/material/IconButton'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import { Banner } from 'common-ui/components/Banner'
import { useBillingApi } from '../../api/billing/billing-context'
import { RtuType, CustomerRtu, Customer } from 'common-billing-server/types'
import { formatDate } from '../../format-date'
import { useFailedOperationBanner } from '../common/hooks/hook-failed-operation'
import { useAuth } from '../login/Auth'
import { Role } from 'common-billing-server/types'
import { isRoleAuthorized } from 'common-billing-server/role'
import { formatRtuPurchaseCsv, licenseTransferLabel, listAllPurchasePages } from 'common-billing-server/purchaseCsv'
import { downloadCsv } from 'common-ui/utils/downloadCsv'
import { TransferLicensesDialog, TransferSource } from './TransferLicensesDialog'

interface CustomerRtuManagementProps {
    customerId: string
    customerName: string
}

export const CustomerRtuManagement = ({ customerId, customerName }: CustomerRtuManagementProps) => {
    const billingApi = useBillingApi()
    const { user } = useAuth()
    const canDeleteRtu = !!user && isRoleAuthorized({ role: user.role, minimumAllowedRole: Role.admin })
    const canTransferRtu = !!user && isRoleAuthorized({ role: user.role, minimumAllowedRole: Role.licenseAdmin })
    const [transferSource, setTransferSource] = useState<TransferSource | null>(null)
    const [notice, setNotice] = useState<string>()
    const [rtus, setRtus] = useState<CustomerRtu[]>([])
    const [rtuTypes, setRtuTypes] = useState<RtuType[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [isDialogOpen, setIsDialogOpen] = useState(false)
    const [selectedRtuType, setSelectedRtuType] = useState('')
    const [quantity, setQuantity] = useState(1)
    const [reference, setReference] = useState('')
    const [isCreating, setIsCreating] = useState(false)
    const [isExporting, setIsExporting] = useState(false)
    const currentCustomerId = useRef(customerId)
    currentCustomerId.current = customerId
    const { addFailedOperation, removeFailedOperation, errorBanner } = useFailedOperationBanner()

    const fetchRtus = async () => {
        const operationId = 'fetch-rtus'
        removeFailedOperation(operationId)
        setIsLoading(true)
        try {
            const response = await billingApi.listCustomerRtus({ filter: { customerId } })
            setRtus(response.items)
        } catch (error) {
            addFailedOperation({
                id: operationId,
                message: 'Failed to fetch RTUs',
                error,
            })
        }
        setIsLoading(false)
    }

    const fetchRtuTypes = async () => {
        try {
            const response = await billingApi.listRtuTypes()
            setRtuTypes(response.items)
        } catch (error) {
            // Failed to fetch RTU types
        }
    }

    useEffect(() => {
        void fetchRtus()
        void fetchRtuTypes()

        // oxlint-disable-next-line react-hooks/exhaustive-deps
    }, [customerId])

    const handleCreateRtu = async () => {
        const operationId = 'create-rtu'
        removeFailedOperation(operationId)
        setIsCreating(true)
        try {
            await billingApi.createCustomerRtu({
                customerId,
                rtuTypeId: selectedRtuType,
                quantity,
                reference,
            })
            setIsDialogOpen(false)
            setSelectedRtuType('')
            setQuantity(1)
            setReference('')
            await fetchRtus()
        } catch (error) {
            addFailedOperation({
                id: operationId,
                message: 'Failed to create RTU',
                error,
            })
        }
        setIsCreating(false)
    }

    const handleDeleteRtu = async (rtuId: string) => {
        const operationId = 'delete-rtu'
        removeFailedOperation(operationId)
        if (!confirm('Are you sure you want to delete this RTU?')) {
            return
        }
        try {
            await billingApi.deleteCustomerRtu(rtuId)
            await fetchRtus()
        } catch (error) {
            addFailedOperation({
                id: operationId,
                message: 'Failed to delete RTU',
                error,
            })
        }
    }

    const handleTransfer = async (source: TransferSource, recipient: Customer, quantity: number) => {
        const operationId = 'transfer-rtus'
        removeFailedOperation(operationId)
        try {
            await billingApi.transferCustomerRtu(source.id, { customerId: recipient.id, quantity })
            setNotice(`Transferred ${quantity} ${source.name} RTUs to ${recipient.name}.`)
            await fetchRtus()
            return true
        } catch (error) {
            addFailedOperation({ id: operationId, message: 'Failed to transfer RTUs', error })
            return false
        }
    }

    const handleExport = async () => {
        const operationId = 'export-rtus'
        removeFailedOperation(operationId)
        setIsExporting(true)
        try {
            const purchases = await listAllPurchasePages(async (skip, limit) => {
                if (currentCustomerId.current !== customerId) throw new Error('Customer changed during export.')
                const page = await billingApi.listCustomerRtus({ filter: { customerId }, skip, limit })
                if (currentCustomerId.current !== customerId) throw new Error('Customer changed during export.')
                return page
            })
            if (currentCustomerId.current !== customerId) throw new Error('Customer changed during export.')
            downloadCsv(`rtu-purchases-${customerId}.csv`, formatRtuPurchaseCsv(purchases, 'billing'))
        } catch (error) {
            addFailedOperation({ id: operationId, message: 'Failed to export RTU purchases', error })
        } finally {
            setIsExporting(false)
        }
    }

    return (
        <Paper sx={{ mt: 2, padding: '20px' }}>
            <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}
            >
                <Typography variant="h6">RTU Purchases</Typography>
                <div style={{ display: 'flex', gap: '8px' }}>
                    <Button
                        onClick={() => void handleExport()}
                        disabled={isLoading || isExporting || rtus.length === 0}
                    >
                        {isExporting ? 'Exporting…' : 'Export CSV'}
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setIsDialogOpen(true)}
                        disabled={isLoading}
                    >
                        Add RTU Purchase
                    </Button>
                </div>
            </div>

            {errorBanner}
            {notice && <Banner id="notice" isOpen={true} message={notice} onClose={() => setNotice(undefined)} />}

            {isLoading ? (
                <CircularProgress />
            ) : rtus.length === 0 ? (
                <Alert severity="info">No RTU purchases found for {customerName}</Alert>
            ) : (
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>RTU Type</TableCell>
                                <TableCell>Quantity</TableCell>
                                <TableCell>Reference</TableCell>
                                <TableCell>Created</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rtus.map((rtu) => (
                                <ActionMenuRow key={rtu.id}>
                                    <TableCell>{rtu.rtuType.name}</TableCell>
                                    <TableCell>{rtu.quantity}</TableCell>
                                    <TableCell>
                                        {rtu.transfer
                                            ? licenseTransferLabel(rtu.transfer, 'billing')
                                            : rtu.reference || '-'}
                                    </TableCell>
                                    <TableCell>{formatDate(new Date(rtu.createdAt))}</TableCell>
                                    <TableCell>
                                        <ActionMenu
                                            options={[
                                                ...(canTransferRtu && rtu.transferable
                                                    ? [
                                                          {
                                                              label: 'Transfer unallocated RTUs',
                                                              icon: <SwapHorizIcon />,
                                                              onClick: () =>
                                                                  setTransferSource({
                                                                      id: rtu.id,
                                                                      name: rtu.rtuType.name,
                                                                      transferable: rtu.transferable ?? 0,
                                                                  }),
                                                          },
                                                      ]
                                                    : []),
                                                ...(canDeleteRtu && !rtu.transfer
                                                    ? [
                                                          {
                                                              label: 'Delete RTU purchase',
                                                              icon: <DeleteIcon />,
                                                              isDestructive: true,
                                                              onClick: () => void handleDeleteRtu(rtu.id),
                                                          },
                                                      ]
                                                    : []),
                                            ]}
                                        >
                                            {canTransferRtu && rtu.transferable ? (
                                                <IconButton
                                                    aria-label={`Transfer ${rtu.rtuType.name} RTUs`}
                                                    title="Transfer unallocated RTUs"
                                                    onClick={() =>
                                                        setTransferSource({
                                                            id: rtu.id,
                                                            name: rtu.rtuType.name,
                                                            transferable: rtu.transferable ?? 0,
                                                        })
                                                    }
                                                    size="small"
                                                >
                                                    <SwapHorizIcon />
                                                </IconButton>
                                            ) : null}
                                            {/* Transfers are ledger entries; deleting either side would create or destroy RTUs. */}
                                            {canDeleteRtu && !rtu.transfer ? (
                                                <IconButton
                                                    aria-label={`Delete ${rtu.rtuType.name} RTU purchase`}
                                                    onClick={() => void handleDeleteRtu(rtu.id)}
                                                    color="error"
                                                    size="small"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            ) : null}
                                        </ActionMenu>
                                    </TableCell>
                                </ActionMenuRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <TransferLicensesDialog
                source={transferSource}
                customerId={customerId}
                customerName={customerName}
                onClose={() => setTransferSource(null)}
                onTransfer={handleTransfer}
            />

            <Dialog
                open={isDialogOpen}
                onClose={() => {
                    setIsDialogOpen(false)
                    setSelectedRtuType('')
                    setQuantity(1)
                    setReference('')
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Add RTU Purchase</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth margin="normal">
                        <InputLabel>RTU Type</InputLabel>
                        <Select
                            value={selectedRtuType}
                            onChange={(e) => setSelectedRtuType(e.target.value)}
                            label="RTU Type"
                        >
                            {rtuTypes.map((type) => (
                                <MenuItem key={type.id} value={type.id}>
                                    {type.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        fullWidth
                        label="Quantity"
                        type="number"
                        value={quantity}
                        onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                        margin="normal"
                        required
                        slotProps={{ htmlInput: { min: 1 } }}
                    />
                    <TextField
                        fullWidth
                        label="Reference (optional)"
                        value={reference}
                        onChange={(e) => setReference(e.target.value)}
                        margin="normal"
                        helperText="Purchase order number or other reference"
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setIsDialogOpen(false)
                            setSelectedRtuType('')
                            setQuantity(1)
                            setReference('')
                        }}
                        disabled={isCreating}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={() => void handleCreateRtu()}
                        variant="contained"
                        disabled={!selectedRtuType || isCreating}
                        startIcon={isCreating ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
                    >
                        Add RTU Purchase
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    )
}
